import React, { ReactElement, Suspense, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { theme } from './utils/theme';
import {
  setUpSessionStorage,
  getClientIdParamValue,
  initiateSplunkRum,
} from './utils/helpers';
import { useAppDispatch } from './store/store';
import {
  isAppReadyWithStaticContent,
  isErrorLoadingContent,
} from './store/contentSlice';
import { setClientId } from './store/sessionSlice';
import AppLayout from './components/appLayout/AppLayout';
import StaticContentLoader from './components/staticContentLoader/StaticContentLoader';
import SynchronyLoader from './components/loader/SynchronyLoader';
import ProcessError from './components/errors/ProcessError';
import LinkNotValidError from './components/errors/LinkNotValidError';
import SystemDownError from './components/errors/SystemDownError';
import CustomerVerification from './components/customerVerification/CustomerVerification';
import AccountList from './components/accountList/AccountList';
import SessionTimeoutError from './components/errors/SessionTimeoutError';
import ThankYou from './components/thankYou/ThankYou';
import AccountNotFoundError from './components/errors/AccountNotFoundError';
import NotAuthenticatedError from './components/errors/NotAuthenticatedError';
import StopByPageError from './components/errors/StopByPageError';
import LowesAccountError from './components/errors/LowesAccountError';

export const App = (): ReactElement => {
  const dispatch = useAppDispatch();
  const clientIdParamValue = getClientIdParamValue().toLowerCase();
  const loadApp = useSelector(isAppReadyWithStaticContent);
  const errorInLoadingContent = useSelector(isErrorLoadingContent);
  useEffect(() => {
    setUpSessionStorage();
    initiateSplunkRum();
    dispatch(setClientId(clientIdParamValue));
  }, []);

  return (
    <Router basename="/">
      <StaticContentLoader />
      <ThemeProvider theme={theme}>
        <Suspense fallback={<SynchronyLoader />}>
          <Routes>
            {loadApp && (
              <>
                <Route
                  element={
                    errorInLoadingContent ? (
                      <Navigate to="/process-error" />
                    ) : (
                      <AppLayout />
                    )
                  }
                >
                  <Route path="/clientid?" element={<CustomerVerification />} />
                  <Route path="/dashboard" element={<AccountList />} />
                  <Route path="/stop-by-page" element={<StopByPageError />} />
                  <Route path="/system-down" element={<SystemDownError />} />
                  <Route
                    path="/account-error"
                    element={<LowesAccountError />}
                  />
                  <Route
                    path="/link-not-valid"
                    element={<LinkNotValidError />}
                  />
                  <Route
                    path="/not-verified"
                    element={<NotAuthenticatedError />}
                  />
                  <Route path="/thank-you" element={<ThankYou />} />
                  <Route path="/welcome" element={<CustomerVerification />} />
                  <Route
                    path="/account-not-found"
                    element={<AccountNotFoundError />}
                  />
                  <Route
                    path="/session-timeout-error"
                    element={<SessionTimeoutError />}
                  />
                  <Route path="*" element={<LinkNotValidError />} />
                </Route>
              </>
            )}
            <Route path="/process-error" element={<ProcessError />} />
          </Routes>
        </Suspense>
      </ThemeProvider>
    </Router>
  );
};
